// SPDX-License-Identifier: Apache-2.0
// File: src/audio/SynthParameters.ts

import { PocketGridRetroPalette } from '../ui/theme/PocketGridRetroTheme';

export type TaperType = 'linear' | 'logarithmic' | 'exponential' | 'bipolar' | 'stepped';

export interface ParamDefinition {
  id: string;
  name: string;
  shortLabel: string;
  category: 'Oscillators' | 'Mixer' | 'Filter' | 'Envelopes' | 'LFO' | 'Master FX' | 'Performance FX' | 'Character Deep' | 'Fine Tune';
  defaultMidiCc: number;
  defaultValue: number; // Normalized 0..1 (for bipolar, 0.5 represents 0)
  displayRange: string;
  unit: string;
  taper: TaperType;
  bipolar?: boolean;
  springBack?: boolean;
  springBackTarget?: number;
  color: string;
  formatDisplay: (normVal: number) => string;
}

// =============================================================================
// 1. TOP 16 PERFORMANCE PARAMETERS (4 Functional Clusters of 4)
// =============================================================================
export const TOP_16_PARAMS: ParamDefinition[] = [
  // ROW 1: Tone & Harmonics (Gold / Amber)
  {
    id: 'cutoff',
    name: 'CUTOFF',
    shortLabel: 'CUTOFF',
    category: 'Filter',
    defaultMidiCc: 74,
    defaultValue: 0.75,
    displayRange: '20 Hz – 20.0 kHz',
    unit: 'Hz',
    taper: 'logarithmic',
    color: PocketGridRetroPalette.tungstenAmber,
    formatDisplay: (v) => {
      const hz = Math.round(20 * Math.pow(1000, v));
      return hz >= 1000 ? `${(hz / 1000).toFixed(1)} kHz` : `${hz} Hz`;
    },
  },
  {
    id: 'resonance',
    name: 'RESONANCE',
    shortLabel: 'RESONANCE',
    category: 'Filter',
    defaultMidiCc: 71,
    defaultValue: 0.15,
    displayRange: '0% – 100%',
    unit: '%',
    taper: 'linear',
    color: PocketGridRetroPalette.tungstenAmber,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  {
    id: 'drive',
    name: 'DRIVE / WARMTH',
    shortLabel: 'DRIVE',
    category: 'Filter',
    defaultMidiCc: 20,
    defaultValue: 0.10,
    displayRange: '0.0 dB – +24.0 dB',
    unit: 'dB',
    taper: 'exponential',
    color: PocketGridRetroPalette.tungstenAmber,
    formatDisplay: (v) => `+${(v * 24).toFixed(1)} dB`,
  },
  {
    id: 'waveShape',
    name: 'WAVE SHAPE',
    shortLabel: 'SHAPE',
    category: 'Oscillators',
    defaultMidiCc: 14,
    defaultValue: 0.33,
    displayRange: 'Sine/FM -> Saw -> Square -> Pulse',
    unit: '',
    taper: 'linear',
    color: PocketGridRetroPalette.tungstenAmber,
    formatDisplay: (v) => (v < 0.25 ? 'Sine/FM' : v < 0.55 ? 'Saw' : v < 0.80 ? 'Square' : 'Pulse'),
  },

  // ROW 2: Dynamic Articulation (Teal / Sage)
  {
    id: 'ampAttack',
    name: 'AMP ATTACK',
    shortLabel: 'AMP ATT',
    category: 'Envelopes',
    defaultMidiCc: 73,
    defaultValue: 0.05,
    displayRange: '1 ms – 10.0 s',
    unit: 's',
    taper: 'logarithmic',
    color: PocketGridRetroPalette.vintageVfdSage,
    formatDisplay: (v) => {
      const ms = Math.round(1 + Math.pow(v, 2.4) * 9999);
      return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`;
    },
  },
  {
    id: 'ampDecay',
    name: 'AMP DECAY',
    shortLabel: 'AMP DEC',
    category: 'Envelopes',
    defaultMidiCc: 75,
    defaultValue: 0.35,
    displayRange: '5 ms – 15.0 s',
    unit: 's',
    taper: 'logarithmic',
    color: PocketGridRetroPalette.vintageVfdSage,
    formatDisplay: (v) => {
      const ms = Math.round(5 + Math.pow(v, 2.1) * 14995);
      return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`;
    },
  },
  {
    id: 'ampSustain',
    name: 'AMP SUSTAIN',
    shortLabel: 'AMP SUS',
    category: 'Envelopes',
    defaultMidiCc: 79,
    defaultValue: 0.80,
    displayRange: '-inf dB – 0.0 dB',
    unit: 'dB',
    taper: 'exponential',
    color: PocketGridRetroPalette.vintageVfdSage,
    formatDisplay: (v) => (v <= 0.001 ? '-inf dB' : `${(20 * Math.log10(v)).toFixed(1)} dB`),
  },
  {
    id: 'ampRelease',
    name: 'AMP RELEASE',
    shortLabel: 'AMP REL',
    category: 'Envelopes',
    defaultMidiCc: 72,
    defaultValue: 0.25,
    displayRange: '10 ms – 20.0 s',
    unit: 's',
    taper: 'logarithmic',
    color: PocketGridRetroPalette.vintageVfdSage,
    formatDisplay: (v) => {
      const ms = Math.round(10 + Math.pow(v, 2.2) * 19990);
      return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`;
    },
  },

  // ROW 3: Animation & Modulation (Coral / Terracotta)
  {
    id: 'fltEnvAmount',
    name: 'FLT ENV AMOUNT',
    shortLabel: 'FLT ENV',
    category: 'Filter',
    defaultMidiCc: 77,
    defaultValue: 0.70, // Represents +40% in bipolar range (0.5 = 0%, 0.7 = +40%)
    displayRange: '-100% – +100%',
    unit: '%',
    taper: 'bipolar',
    bipolar: true,
    color: PocketGridRetroPalette.burntTerracotta,
    formatDisplay: (v) => {
      const pct = Math.round((v - 0.5) * 200);
      return `${pct >= 0 ? '+' : ''}${pct}%`;
    },
  },
  {
    id: 'fltDecay',
    name: 'FLT DECAY',
    shortLabel: 'FLT DEC',
    category: 'Filter',
    defaultMidiCc: 78,
    defaultValue: 0.30,
    displayRange: '5 ms – 15.0 s',
    unit: 's',
    taper: 'logarithmic',
    color: PocketGridRetroPalette.burntTerracotta,
    formatDisplay: (v) => {
      const ms = Math.round(5 + Math.pow(v, 2.1) * 14995);
      return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`;
    },
  },
  {
    id: 'lfoRate',
    name: 'LFO RATE',
    shortLabel: 'LFO RATE',
    category: 'LFO',
    defaultMidiCc: 76,
    defaultValue: 0.25,
    displayRange: '0.05 Hz – 30.0 Hz',
    unit: 'Hz',
    taper: 'logarithmic',
    color: PocketGridRetroPalette.burntTerracotta,
    formatDisplay: (v) => `${(0.05 + Math.pow(v, 2.2) * 29.95).toFixed(2)} Hz`,
  },
  {
    id: 'lfoDepth',
    name: 'LFO DEPTH',
    shortLabel: 'LFO DEPTH',
    category: 'LFO',
    defaultMidiCc: 1,
    defaultValue: 0.00,
    displayRange: '0% – 100%',
    unit: '%',
    taper: 'linear',
    color: PocketGridRetroPalette.burntTerracotta,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },

  // ROW 4: Space, Drive & Master Output (Warm Gray)
  {
    id: 'oscBalance',
    name: 'OSC MIX / SUB',
    shortLabel: 'OSC MIX',
    category: 'Mixer',
    defaultMidiCc: 15,
    defaultValue: 0.20,
    displayRange: 'Osc 1 (0) / Sub (100)',
    unit: '%',
    taper: 'linear',
    color: PocketGridRetroPalette.champagneBrass,
    formatDisplay: (v) => `${Math.round((1 - v) * 100)}/${Math.round(v * 100)}`,
  },
  {
    id: 'detune',
    name: 'UNISON DETUNE',
    shortLabel: 'DETUNE',
    category: 'Oscillators',
    defaultMidiCc: 94,
    defaultValue: 0.10,
    displayRange: '0.0 ct – 50.0 ct',
    unit: 'ct',
    taper: 'linear',
    color: PocketGridRetroPalette.champagneBrass,
    formatDisplay: (v) => `${(v * 50).toFixed(1)} ct`,
  },
  {
    id: 'delayMix',
    name: 'DELAY MIX',
    shortLabel: 'DELAY',
    category: 'Master FX',
    defaultMidiCc: 93,
    defaultValue: 0.15,
    displayRange: '0.0% – 100.0%',
    unit: '%',
    taper: 'linear',
    color: PocketGridRetroPalette.champagneBrass,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  {
    id: 'reverbSpace',
    name: 'REVERB SPACE',
    shortLabel: 'REVERB',
    category: 'Master FX',
    defaultMidiCc: 91,
    defaultValue: 0.25,
    displayRange: '0.0% – 100.0%',
    unit: '%',
    taper: 'linear',
    color: PocketGridRetroPalette.champagneBrass,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
];

// =============================================================================
// 2. TOP 32 EXTENDED STUDIO SYNTHESIS MATRIX (6 Modular Strips)
// =============================================================================
export const TOP_32_PARAMS: ParamDefinition[] = [
  // SECTION 1: OSCILLATORS (Gold) - 8 controls
  {
    id: 'osc1Wave',
    name: 'OSC 1 WAVE',
    shortLabel: 'O1 WAVE',
    category: 'Oscillators',
    defaultMidiCc: 14,
    defaultValue: 0.33,
    displayRange: 'Sine/FM / Saw / Square / Pulse',
    unit: '',
    taper: 'stepped',
    color: PocketGridRetroPalette.tungstenAmber,
    formatDisplay: (v) => (v < 0.25 ? 'Sine/FM' : v < 0.55 ? 'Saw' : v < 0.80 ? 'Square' : 'Pulse'),
  },
  {
    id: 'osc1Octave',
    name: 'OSC 1 OCTAVE',
    shortLabel: 'O1 OCT',
    category: 'Oscillators',
    defaultMidiCc: 40,
    defaultValue: 0.50, // 8' Base (0..4 maps to 32', 16', 8', 4', 2')
    displayRange: "32' / 16' / 8' / 4' / 2'",
    unit: "'",
    taper: 'stepped',
    color: PocketGridRetroPalette.tungstenAmber,
    formatDisplay: (v) => {
      const idx = Math.min(4, Math.floor(v * 5));
      return ["32'", "16'", "8'", "4'", "2'"][idx];
    },
  },
  {
    id: 'osc2Wave',
    name: 'OSC 2 WAVE',
    shortLabel: 'O2 WAVE',
    category: 'Oscillators',
    defaultMidiCc: 41,
    defaultValue: 0.66,
    displayRange: 'Saw / Square / Pulse',
    unit: '',
    taper: 'stepped',
    color: PocketGridRetroPalette.tungstenAmber,
    formatDisplay: (v) => (v < 0.33 ? 'Saw' : v < 0.66 ? 'Square' : 'Pulse'),
  },
  {
    id: 'osc2Octave',
    name: 'OSC 2 OCTAVE',
    shortLabel: 'O2 OCT',
    category: 'Oscillators',
    defaultMidiCc: 42,
    defaultValue: 0.50,
    displayRange: "32' / 16' / 8' / 4' / 2'",
    unit: "'",
    taper: 'stepped',
    color: PocketGridRetroPalette.tungstenAmber,
    formatDisplay: (v) => {
      const idx = Math.min(4, Math.floor(v * 5));
      return ["32'", "16'", "8'", "4'", "2'"][idx];
    },
  },
  {
    id: 'osc2Interval',
    name: 'OSC 2 INTERVAL',
    shortLabel: 'O2 SEMI',
    category: 'Oscillators',
    defaultMidiCc: 43,
    defaultValue: 0.50, // 0 st
    displayRange: '-24 st – +24 st',
    unit: 'st',
    taper: 'bipolar',
    bipolar: true,
    color: PocketGridRetroPalette.tungstenAmber,
    formatDisplay: (v) => {
      const semis = Math.round((v - 0.5) * 48);
      return `${semis >= 0 ? '+' : ''}${semis} st`;
    },
  },
  {
    id: 'osc2Detune',
    name: 'OSC 2 DETUNE',
    shortLabel: 'O2 FINE',
    category: 'Oscillators',
    defaultMidiCc: 44,
    defaultValue: 0.50, // 0.0 ct (in-tune by default)
    displayRange: '-50.0 ct – +50.0 ct',
    unit: 'ct',
    taper: 'bipolar',
    bipolar: true,
    color: PocketGridRetroPalette.tungstenAmber,
    formatDisplay: (v) => {
      const ct = (v - 0.5) * 100;
      return `${ct >= 0 ? '+' : ''}${ct.toFixed(1)} ct`;
    },
  },
  {
    id: 'pulseWidth',
    name: 'PULSE WIDTH',
    shortLabel: 'PW/FOLD',
    category: 'Oscillators',
    defaultMidiCc: 45,
    defaultValue: 0.50,
    displayRange: '5.0% – 95.0%',
    unit: '%',
    taper: 'linear',
    color: PocketGridRetroPalette.tungstenAmber,
    formatDisplay: (v) => `${Math.round(5 + v * 90)}%`,
  },
  {
    id: 'unisonDetune',
    name: 'UNISON DETUNE',
    shortLabel: 'UNISON',
    category: 'Oscillators',
    defaultMidiCc: 94,
    defaultValue: 0.15,
    displayRange: '0 ct – 100 ct',
    unit: 'ct',
    taper: 'exponential',
    color: PocketGridRetroPalette.tungstenAmber,
    formatDisplay: (v) => `${Math.round(v * 100)} ct`,
  },

  // SECTION 2: MIXER & PRE-DRIVE (Amber) - 4 controls
  {
    id: 'osc1Level',
    name: 'OSC 1 LEVEL',
    shortLabel: 'OSC 1 LVL',
    category: 'Mixer',
    defaultMidiCc: 46,
    defaultValue: 0.80,
    displayRange: '-inf dB – 0.0 dB',
    unit: 'dB',
    taper: 'exponential',
    color: PocketGridRetroPalette.champagneBrass,
    formatDisplay: (v) => (v <= 0.001 ? '-inf dB' : `${(20 * Math.log10(v)).toFixed(1)} dB`),
  },
  {
    id: 'osc2Level',
    name: 'OSC 2 LEVEL',
    shortLabel: 'OSC 2 LVL',
    category: 'Mixer',
    defaultMidiCc: 47,
    defaultValue: 0.35,
    displayRange: '-inf dB – 0.0 dB',
    unit: 'dB',
    taper: 'exponential',
    color: PocketGridRetroPalette.champagneBrass,
    formatDisplay: (v) => (v <= 0.001 ? '-inf dB' : `${(20 * Math.log10(v)).toFixed(1)} dB`),
  },
  {
    id: 'subLevel',
    name: 'SUB LEVEL',
    shortLabel: 'SUB LVL',
    category: 'Mixer',
    defaultMidiCc: 48,
    defaultValue: 0.20,
    displayRange: '-inf dB – 0.0 dB',
    unit: 'dB',
    taper: 'exponential',
    color: PocketGridRetroPalette.champagneBrass,
    formatDisplay: (v) => (v <= 0.001 ? '-inf dB' : `${(20 * Math.log10(v)).toFixed(1)} dB`),
  },
  {
    id: 'noiseLevel',
    name: 'NOISE LEVEL',
    shortLabel: 'NOISE LVL',
    category: 'Mixer',
    defaultMidiCc: 49,
    defaultValue: 0.00,
    displayRange: '-inf dB – 0.0 dB',
    unit: 'dB',
    taper: 'exponential',
    color: PocketGridRetroPalette.champagneBrass,
    formatDisplay: (v) => (v <= 0.001 ? '-inf dB' : `${(20 * Math.log10(v)).toFixed(1)} dB`),
  },

  // SECTION 3: VCF / FILTER LAB (Teal) - 5 controls
  {
    id: 'cutoff',
    name: 'CUTOFF',
    shortLabel: 'CUTOFF',
    category: 'Filter',
    defaultMidiCc: 74,
    defaultValue: 0.70,
    displayRange: '20 Hz – 20.0 kHz',
    unit: 'Hz',
    taper: 'logarithmic',
    color: PocketGridRetroPalette.vintageVfdSage,
    formatDisplay: (v) => {
      const hz = Math.round(20 * Math.pow(1000, v));
      return hz >= 1000 ? `${(hz / 1000).toFixed(1)} kHz` : `${hz} Hz`;
    },
  },
  {
    id: 'resonance',
    name: 'RESONANCE',
    shortLabel: 'RESONANCE',
    category: 'Filter',
    defaultMidiCc: 71,
    defaultValue: 0.10,
    displayRange: '0% – 100%',
    unit: '%',
    taper: 'linear',
    color: PocketGridRetroPalette.vintageVfdSage,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  {
    id: 'filterDrive',
    name: 'FILTER DRIVE',
    shortLabel: 'DRIVE',
    category: 'Filter',
    defaultMidiCc: 20,
    defaultValue: 0.00,
    displayRange: '0 dB – +24 dB',
    unit: 'dB',
    taper: 'exponential',
    color: PocketGridRetroPalette.vintageVfdSage,
    formatDisplay: (v) => `+${(v * 24).toFixed(1)} dB`,
  },
  {
    id: 'keyTracking',
    name: 'KEY TRACKING',
    shortLabel: 'KEYTRACK',
    category: 'Filter',
    defaultMidiCc: 50,
    defaultValue: 0.50,
    displayRange: '0% – 100%',
    unit: '%',
    taper: 'linear',
    color: PocketGridRetroPalette.vintageVfdSage,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  {
    id: 'fltEnvDepth',
    name: 'FLT ENV DEPTH',
    shortLabel: 'ENV DEPTH',
    category: 'Filter',
    defaultMidiCc: 77,
    defaultValue: 0.75, // +50%
    displayRange: '-100% – +100%',
    unit: '%',
    taper: 'bipolar',
    bipolar: true,
    color: PocketGridRetroPalette.vintageVfdSage,
    formatDisplay: (v) => {
      const pct = Math.round((v - 0.5) * 200);
      return `${pct >= 0 ? '+' : ''}${pct}%`;
    },
  },

  // SECTION 4: AMP ADSR (Coral) - 4 controls
  {
    id: 'ampAttack',
    name: 'AMP ATTACK',
    shortLabel: 'AMP ATT',
    category: 'Envelopes',
    defaultMidiCc: 73,
    defaultValue: 0.05,
    displayRange: '1 ms – 10.0 s',
    unit: 's',
    taper: 'logarithmic',
    color: PocketGridRetroPalette.burntTerracotta,
    formatDisplay: (v) => {
      const ms = Math.round(1 + Math.pow(v, 2.4) * 9999);
      return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`;
    },
  },
  {
    id: 'ampDecay',
    name: 'AMP DECAY',
    shortLabel: 'AMP DEC',
    category: 'Envelopes',
    defaultMidiCc: 75,
    defaultValue: 0.45,
    displayRange: '5 ms – 15.0 s',
    unit: 's',
    taper: 'logarithmic',
    color: PocketGridRetroPalette.burntTerracotta,
    formatDisplay: (v) => {
      const ms = Math.round(5 + Math.pow(v, 2.1) * 14995);
      return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`;
    },
  },
  {
    id: 'ampSustain',
    name: 'AMP SUSTAIN',
    shortLabel: 'AMP SUS',
    category: 'Envelopes',
    defaultMidiCc: 79,
    defaultValue: 1.00,
    displayRange: '-inf dB – 0.0 dB',
    unit: 'dB',
    taper: 'exponential',
    color: PocketGridRetroPalette.burntTerracotta,
    formatDisplay: (v) => (v <= 0.001 ? '-inf dB' : `${(20 * Math.log10(v)).toFixed(1)} dB`),
  },
  {
    id: 'ampRelease',
    name: 'AMP RELEASE',
    shortLabel: 'AMP REL',
    category: 'Envelopes',
    defaultMidiCc: 72,
    defaultValue: 0.20,
    displayRange: '10 ms – 20.0 s',
    unit: 's',
    taper: 'logarithmic',
    color: PocketGridRetroPalette.burntTerracotta,
    formatDisplay: (v) => {
      const ms = Math.round(10 + Math.pow(v, 2.2) * 19990);
      return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`;
    },
  },

  // SECTION 5: FILTER ADSR (Coral) - 4 controls
  {
    id: 'fltAttack',
    name: 'FLT ATTACK',
    shortLabel: 'FLT ATT',
    category: 'Envelopes',
    defaultMidiCc: 51,
    defaultValue: 0.10,
    displayRange: '1 ms – 10.0 s',
    unit: 's',
    taper: 'logarithmic',
    color: PocketGridRetroPalette.burntTerracotta,
    formatDisplay: (v) => {
      const ms = Math.round(1 + Math.pow(v, 2.4) * 9999);
      return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`;
    },
  },
  {
    id: 'fltDecay',
    name: 'FLT DECAY',
    shortLabel: 'FLT DEC',
    category: 'Envelopes',
    defaultMidiCc: 78,
    defaultValue: 0.35,
    displayRange: '5 ms – 15.0 s',
    unit: 's',
    taper: 'logarithmic',
    color: PocketGridRetroPalette.burntTerracotta,
    formatDisplay: (v) => {
      const ms = Math.round(5 + Math.pow(v, 2.1) * 14995);
      return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`;
    },
  },
  {
    id: 'fltSustain',
    name: 'FLT SUSTAIN',
    shortLabel: 'FLT SUS',
    category: 'Envelopes',
    defaultMidiCc: 52,
    defaultValue: 0.20,
    displayRange: '0% – 100%',
    unit: '%',
    taper: 'linear',
    color: PocketGridRetroPalette.burntTerracotta,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  {
    id: 'fltRelease',
    name: 'FLT RELEASE',
    shortLabel: 'FLT REL',
    category: 'Envelopes',
    defaultMidiCc: 53,
    defaultValue: 0.20,
    displayRange: '10 ms – 20.0 s',
    unit: 's',
    taper: 'logarithmic',
    color: PocketGridRetroPalette.burntTerracotta,
    formatDisplay: (v) => {
      const ms = Math.round(10 + Math.pow(v, 2.2) * 19990);
      return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`;
    },
  },

  // SECTION 6: LFO / FX / PERFORMANCE (Warm Gray) - 7 controls
  {
    id: 'lfo1Rate',
    name: 'LFO 1 RATE',
    shortLabel: 'LFO1 RATE',
    category: 'LFO',
    defaultMidiCc: 76,
    defaultValue: 0.35,
    displayRange: '0.05 Hz – 30.0 Hz',
    unit: 'Hz',
    taper: 'logarithmic',
    color: PocketGridRetroPalette.champagneBrass,
    formatDisplay: (v) => `${(0.05 + Math.pow(v, 2.2) * 29.95).toFixed(2)} Hz`,
  },
  {
    id: 'lfo1Depth',
    name: 'LFO 1 DEPTH',
    shortLabel: 'LFO1->CUT',
    category: 'LFO',
    defaultMidiCc: 1,
    defaultValue: 0.00,
    displayRange: '0% – 100%',
    unit: '%',
    taper: 'linear',
    color: PocketGridRetroPalette.champagneBrass,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  {
    id: 'lfo2Rate',
    name: 'LFO 2 RATE',
    shortLabel: 'LFO2 RATE',
    category: 'LFO',
    defaultMidiCc: 54,
    defaultValue: 0.20,
    displayRange: '0.01 Hz – 20.0 Hz',
    unit: 'Hz',
    taper: 'logarithmic',
    color: PocketGridRetroPalette.champagneBrass,
    formatDisplay: (v) => `${(0.01 + Math.pow(v, 2.4) * 19.99).toFixed(2)} Hz`,
  },
  {
    id: 'lfo2ToPwm',
    name: 'LFO 2 -> PWM',
    shortLabel: 'LFO2->PWM',
    category: 'LFO',
    defaultMidiCc: 55,
    defaultValue: 0.00,
    displayRange: '0% – 100%',
    unit: '%',
    taper: 'linear',
    color: PocketGridRetroPalette.champagneBrass,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  {
    id: 'portamento',
    name: 'PORTAMENTO',
    shortLabel: 'GLIDE',
    category: 'Performance FX',
    defaultMidiCc: 5,
    defaultValue: 0.00,
    displayRange: '0 ms – 2000 ms',
    unit: 'ms',
    taper: 'exponential',
    color: PocketGridRetroPalette.champagneBrass,
    formatDisplay: (v) => (v <= 0.01 ? 'Off' : `${Math.round(Math.pow(v, 2) * 2000)} ms`),
  },
  {
    id: 'delayTimeMix',
    name: 'DELAY TIME/MIX',
    shortLabel: 'DELAY',
    category: 'Master FX',
    defaultMidiCc: 93,
    defaultValue: 0.00,
    displayRange: '0% – 100%',
    unit: '%',
    taper: 'linear',
    color: PocketGridRetroPalette.champagneBrass,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  {
    id: 'reverbSpace',
    name: 'REVERB SPACE',
    shortLabel: 'REVERB',
    category: 'Master FX',
    defaultMidiCc: 91,
    defaultValue: 0.20,
    displayRange: '0% – 100%',
    unit: '%',
    taper: 'linear',
    color: PocketGridRetroPalette.champagneBrass,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
];

// =============================================================================
// 3. DEEP MENU: CHARACTER & PHYSICAL RESONATORS (Original 16 with Clean CC 102–117)
// =============================================================================
export const DEEP_CHARACTER_PARAMS: ParamDefinition[] = [
  {
    id: 'algMorph',
    name: 'ALG MORPH',
    shortLabel: 'ALG MORPH',
    category: 'Character Deep',
    defaultMidiCc: 102,
    defaultValue: 0.55,
    displayRange: '0% – 100%',
    unit: '%',
    taper: 'linear',
    color: PocketGridRetroPalette.tungstenAmber,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  {
    id: 'waveFold',
    name: 'WAVE FOLD',
    shortLabel: 'WAVE FOLD',
    category: 'Character Deep',
    defaultMidiCc: 103,
    defaultValue: 0.38,
    displayRange: '0% – 100%',
    unit: '%',
    taper: 'linear',
    color: PocketGridRetroPalette.tungstenAmber,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  {
    id: 'fmFeedback',
    name: 'FM FEEDBACK',
    shortLabel: 'FM FEEDBK',
    category: 'Character Deep',
    defaultMidiCc: 104,
    defaultValue: 0.30,
    displayRange: '0% – 100%',
    unit: '%',
    taper: 'linear',
    color: PocketGridRetroPalette.tungstenAmber,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  {
    id: 'formantShift',
    name: 'FORMANT SHIFT',
    shortLabel: 'FORMANT',
    category: 'Character Deep',
    defaultMidiCc: 105,
    defaultValue: 0.50,
    displayRange: '-24 st – +24 st',
    unit: 'st',
    taper: 'bipolar',
    bipolar: true,
    color: PocketGridRetroPalette.tungstenAmber,
    formatDisplay: (v) => {
      const st = Math.round((v - 0.5) * 48);
      return `${st >= 0 ? '+' : ''}${st} st`;
    },
  },
  {
    id: 'vowelMorphX',
    name: 'VOWEL MORPH X',
    shortLabel: 'VOWEL X',
    category: 'Character Deep',
    defaultMidiCc: 106,
    defaultValue: 0.55,
    displayRange: '0% – 100% (A-E-I)',
    unit: '',
    taper: 'linear',
    color: PocketGridRetroPalette.vintageVfdSage,
    formatDisplay: (v) => (v < 0.33 ? 'Vowel [A]' : v < 0.66 ? 'Vowel [E]' : 'Vowel [I]'),
  },
  {
    id: 'vowelMorphY',
    name: 'VOWEL MORPH Y',
    shortLabel: 'VOWEL Y',
    category: 'Character Deep',
    defaultMidiCc: 107,
    defaultValue: 0.56,
    displayRange: '0% – 100% (O-U)',
    unit: '',
    taper: 'linear',
    color: PocketGridRetroPalette.vintageVfdSage,
    formatDisplay: (v) => (v < 0.5 ? 'Vowel [O]' : 'Vowel [U]'),
  },
  {
    id: 'modAttack',
    name: 'MOD ATTACK',
    shortLabel: 'MOD ATT',
    category: 'Character Deep',
    defaultMidiCc: 108,
    defaultValue: 0.38,
    displayRange: '1 ms – 10.0 s',
    unit: 's',
    taper: 'logarithmic',
    color: PocketGridRetroPalette.vintageVfdSage,
    formatDisplay: (v) => {
      const ms = Math.round(1 + Math.pow(v, 2.4) * 9999);
      return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`;
    },
  },
  {
    id: 'modDecay',
    name: 'MOD DECAY',
    shortLabel: 'MOD DEC',
    category: 'Character Deep',
    defaultMidiCc: 109,
    defaultValue: 0.84,
    displayRange: '5 ms – 15.0 s',
    unit: 's',
    taper: 'logarithmic',
    color: PocketGridRetroPalette.vintageVfdSage,
    formatDisplay: (v) => {
      const ms = Math.round(5 + Math.pow(v, 2.1) * 14995);
      return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`;
    },
  },
  {
    id: 'carAttack',
    name: 'CARRIER ATTACK',
    shortLabel: 'CAR ATT',
    category: 'Character Deep',
    defaultMidiCc: 110,
    defaultValue: 0.34,
    displayRange: '1 ms – 10.0 s',
    unit: 's',
    taper: 'logarithmic',
    color: PocketGridRetroPalette.burntTerracotta,
    formatDisplay: (v) => {
      const ms = Math.round(1 + Math.pow(v, 2.4) * 9999);
      return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`;
    },
  },
  {
    id: 'carDecay',
    name: 'CARRIER DECAY',
    shortLabel: 'CAR DEC',
    category: 'Character Deep',
    defaultMidiCc: 111,
    defaultValue: 0.86,
    displayRange: '5 ms – 15.0 s',
    unit: 's',
    taper: 'logarithmic',
    color: PocketGridRetroPalette.burntTerracotta,
    formatDisplay: (v) => {
      const ms = Math.round(5 + Math.pow(v, 2.1) * 14995);
      return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`;
    },
  },
  {
    id: 'chorusDelayMix',
    name: 'CHORUS + DELAY',
    shortLabel: 'CHORUS',
    category: 'Character Deep',
    defaultMidiCc: 112,
    defaultValue: 0.42,
    displayRange: '0% – 100%',
    unit: '%',
    taper: 'linear',
    color: PocketGridRetroPalette.burntTerracotta,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  {
    id: 'spaceReverbMix',
    name: 'SPACE REVERB',
    shortLabel: 'REVERB',
    category: 'Character Deep',
    defaultMidiCc: 113,
    defaultValue: 0.50,
    displayRange: '0% – 100%',
    unit: '%',
    taper: 'linear',
    color: PocketGridRetroPalette.burntTerracotta,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  {
    id: 'resonatorDecay',
    name: 'RESONATOR DECAY',
    shortLabel: 'RES DECAY',
    category: 'Character Deep',
    defaultMidiCc: 114,
    defaultValue: 0.13,
    displayRange: '10 ms – 30.0 s',
    unit: 's',
    taper: 'logarithmic',
    color: PocketGridRetroPalette.champagneBrass,
    formatDisplay: (v) => {
      const ms = Math.round(10 + Math.pow(v, 2.4) * 29990);
      return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`;
    },
  },
  {
    id: 'drumDispersion',
    name: 'MEMBRANE DISPERSION',
    shortLabel: 'MEMBRANE',
    category: 'Character Deep',
    defaultMidiCc: 115,
    defaultValue: 0.77,
    displayRange: '0% – 100%',
    unit: '%',
    taper: 'linear',
    color: PocketGridRetroPalette.champagneBrass,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  {
    id: 'wowFlutter',
    name: 'WOW & FLUTTER',
    shortLabel: 'WOW/FLUT',
    category: 'Character Deep',
    defaultMidiCc: 116,
    defaultValue: 0.36,
    displayRange: '0.0 Hz – 10.0 Hz',
    unit: 'Hz',
    taper: 'linear',
    color: PocketGridRetroPalette.champagneBrass,
    formatDisplay: (v) => `${(v * 10.0).toFixed(1)} Hz`,
  },
  {
    id: 'limiterDrive',
    name: 'TAPE DRIVE',
    shortLabel: 'TAPE DRV',
    category: 'Character Deep',
    defaultMidiCc: 117,
    defaultValue: 0.50,
    displayRange: '0 dB – +18 dB',
    unit: 'dB',
    taper: 'exponential',
    color: PocketGridRetroPalette.champagneBrass,
    formatDisplay: (v) => `+${(v * 18).toFixed(1)} dB`,
  },
];

// =============================================================================
// 4. LIVE PERFORMANCE FX SLIDERS (Momentary Spring-Back vs Hold Latch)
// =============================================================================
export const PERFORMANCE_FX_PARAMS: ParamDefinition[] = [
  {
    id: 'perfDjFilter',
    name: 'DJ DUAL FILTER',
    shortLabel: 'DJ FILTER',
    category: 'Performance FX',
    defaultMidiCc: 60,
    defaultValue: 0.50, // Bipolar: Center 0.5 = Neutral (Off). < 0.5 = LPF, > 0.5 = HPF
    displayRange: 'LPF <- NEUTRAL -> HPF',
    unit: '',
    taper: 'bipolar',
    bipolar: true,
    springBack: true,
    springBackTarget: 0.50,
    color: PocketGridRetroPalette.vintageVfdSage,
    formatDisplay: (v) => {
      if (Math.abs(v - 0.5) < 0.03) return 'NEUTRAL';
      if (v < 0.5) {
        const sweep = Math.round((0.5 - v) * 200);
        return `LPF -${sweep}%`;
      } else {
        const sweep = Math.round((v - 0.5) * 200);
        return `HPF +${sweep}%`;
      }
    },
  },
  {
    id: 'perfStutter',
    name: 'BEAT STUTTER',
    shortLabel: 'STUTTER',
    category: 'Performance FX',
    defaultMidiCc: 61,
    defaultValue: 0.00,
    displayRange: '1/8 -> 1/16 -> 1/32 roll',
    unit: '',
    taper: 'linear',
    springBack: true,
    springBackTarget: 0.00,
    color: PocketGridRetroPalette.burntTerracotta,
    formatDisplay: (v) => {
      if (v < 0.02) return 'OFF';
      if (v < 0.35) return '1/8 Loop';
      if (v < 0.7) return '1/16 Stutter';
      return '1/32 Glitch Roll';
    },
  },
  {
    id: 'perfRevDelay',
    name: 'REVERSE TEMPO DELAY',
    shortLabel: 'REV DELAY',
    category: 'Performance FX',
    defaultMidiCc: 62,
    defaultValue: 0.00,
    displayRange: '0% – 100%',
    unit: '%',
    taper: 'linear',
    springBack: true,
    springBackTarget: 0.00,
    color: PocketGridRetroPalette.champagneBrass,
    formatDisplay: (v) => (v < 0.02 ? 'OFF' : `${Math.round(v * 100)}% REV`),
  },
  {
    id: 'perfBeatGate',
    name: 'CHOPPER BEAT GATE',
    shortLabel: 'GATE',
    category: 'Performance FX',
    defaultMidiCc: 63,
    defaultValue: 0.00,
    displayRange: '0% – 100% (16th Chop)',
    unit: '%',
    taper: 'linear',
    springBack: true,
    springBackTarget: 0.00,
    color: PocketGridRetroPalette.tungstenAmber,
    formatDisplay: (v) => (v < 0.02 ? 'OFF' : `${Math.round(v * 100)}% 1/16`),
  },
  {
    id: 'perfPunchComp',
    name: 'PUNCH COMPRESSOR',
    shortLabel: 'PUNCH COMP',
    category: 'Performance FX',
    defaultMidiCc: 64,
    defaultValue: 0.00,
    displayRange: '0% – 100% Sidechain Pump',
    unit: '%',
    taper: 'linear',
    springBack: true,
    springBackTarget: 0.00,
    color: PocketGridRetroPalette.studioTapeRed,
    formatDisplay: (v) => (v < 0.02 ? 'OFF' : `PUMP ${Math.round(v * 100)}%`),
  },
  {
    id: 'perfPitchBend',
    name: 'PITCH GLIDE BEND',
    shortLabel: 'PITCH BEND',
    category: 'Performance FX',
    defaultMidiCc: 65,
    defaultValue: 0.50, // Center 0.5 = 0 semitones
    displayRange: '-12 st <- 0 -> +12 st',
    unit: 'st',
    taper: 'bipolar',
    bipolar: true,
    springBack: true,
    springBackTarget: 0.50,
    color: PocketGridRetroPalette.tungstenAmber,
    formatDisplay: (v) => {
      const st = Math.round((v - 0.5) * 24);
      return st === 0 ? '0 st' : `${st > 0 ? '+' : ''}${st} st`;
    },
  },
  {
    id: 'perfTapeStop',
    name: 'VINYL TAPE STOP',
    shortLabel: 'TAPE STOP',
    category: 'Performance FX',
    defaultMidiCc: 66,
    defaultValue: 0.00,
    displayRange: 'Brake Speed (0% – 100%)',
    unit: '',
    taper: 'exponential',
    springBack: true,
    springBackTarget: 0.00,
    color: PocketGridRetroPalette.champagneBrass,
    formatDisplay: (v) => (v < 0.02 ? 'RUNNING' : `BRAKE ${Math.round(v * 100)}%`),
  },
];

// Unified map of all available parameters by ID
export const ALL_SYNTH_PARAMS: Record<string, ParamDefinition> = {};
[...TOP_16_PARAMS, ...TOP_32_PARAMS, ...DEEP_CHARACTER_PARAMS, ...PERFORMANCE_FX_PARAMS].forEach((p) => {
  ALL_SYNTH_PARAMS[p.id] = p;
});

// Default initial parameter values
export function getInitialParamValues(): Record<string, number> {
  const vals: Record<string, number> = {};
  Object.values(ALL_SYNTH_PARAMS).forEach((p) => {
    vals[p.id] = p.defaultValue;
  });
  return vals;
}

// Default MIDI CC mapping table
export function getInitialMidiCcMap(): Record<string, number> {
  const map: Record<string, number> = {};
  Object.values(ALL_SYNTH_PARAMS).forEach((p) => {
    map[p.id] = p.defaultMidiCc;
  });
  return map;
}

// Synchronize parameter aliases across Top 16, Top 32 (Studio Matrix), and Character Deep views
// so moving a slider in any view keeps all linked sliders in sync AND auto-enables muted sub-sections when tweaked!
const PARAM_ALIAS_GROUPS: string[][] = [
  ['cutoff', 'toneBrightness'],
  ['resonance', 'bodyResonance'],
  ['drive', 'filterDrive', 'soundboardWarmth'],
  ['waveShape', 'osc1Wave'],
  ['ampAttack', 'carAttack'],
  ['ampDecay', 'carDecay'],
  ['fltEnvAmount', 'fltEnvDepth'],
  ['fltAttack', 'modAttack'],
  ['fltDecay', 'modDecay'],
  ['lfoRate', 'lfo1Rate'],
  ['lfoDepth', 'lfo1Depth'],
  ['oscBalance', 'subLevel'],
  ['detune', 'unisonDetune'],
  ['delayMix', 'delayTimeMix', 'chorusDelayMix'],
  ['reverbSpace', 'spaceReverbMix'],
  ['noiseLevel', 'sibilance'],
];

export function getSynchronizedParamUpdates(
  paramId: string,
  val: number,
  currentValues?: Record<string, number>
): Record<string, number> {
  const clamped = Math.max(0.0, Math.min(1.0, val));
  const updates: Record<string, number> = { [paramId]: clamped };

  for (const group of PARAM_ALIAS_GROUPS) {
    if (group.includes(paramId)) {
      for (const alias of group) {
        updates[alias] = clamped;
      }
    }
  }

  // If the user tweaks an Oscillator 2 parameter in Studio Matrix while Osc 2 Level is near 0,
  // automatically bring Osc 2 Level up to an audible level (0.40) so the change is immediately heard!
  if (
    (paramId === 'osc2Wave' ||
      paramId === 'osc2Octave' ||
      paramId === 'osc2Interval' ||
      paramId === 'osc2Detune') &&
    currentValues &&
    (currentValues.osc2Level ?? 0) < 0.15
  ) {
    updates.osc2Level = 0.42;
  }

  // If the user adjusts LFO 1 Rate while LFO 1 Depth is 0%, automatically bring Depth up to 28% so LFO 1 is heard!
  if (
    (paramId === 'lfoRate' || paramId === 'lfo1Rate') &&
    currentValues &&
    (currentValues.lfoDepth ?? currentValues.lfo1Depth ?? 0) < 0.05
  ) {
    updates.lfoDepth = 0.28;
    updates.lfo1Depth = 0.28;
  }

  // If the user adjusts LFO 2 Rate while LFO 2 -> PWM is 0%, automatically bring LFO 2 -> PWM up to 35% so LFO 2 is heard!
  if (
    paramId === 'lfo2Rate' &&
    currentValues &&
    (currentValues.lfo2ToPwm ?? 0) < 0.05
  ) {
    updates.lfo2ToPwm = 0.35;
  }

  return updates;
}

// Expand a preset's params dictionary so Top 16, Top 32 (Studio Matrix), and Character Deep sliders
// all reflect the loaded preset's actual sound character immediately!
export function expandPresetParamsToAllViews(
  presetParams?: Partial<Record<string, number>>
): Record<string, number> {
  const base = getInitialParamValues();
  if (!presetParams) return base;

  // First copy direct keys
  for (const [k, v] of Object.entries(presetParams)) {
    if (typeof v === 'number' && !Number.isNaN(v)) {
      base[k] = Math.max(0.0, Math.min(1.0, v));
    }
  }

  // Map Character Deep preset keys to Top 16 & Top 32 Studio Matrix keys when not explicitly specified
  if (presetParams.formantShift !== undefined && presetParams.cutoff === undefined) {
    base.cutoff = presetParams.formantShift;
  }
  if (presetParams.algMorph !== undefined && presetParams.waveShape === undefined && presetParams.osc1Wave === undefined) {
    base.waveShape = presetParams.algMorph;
    base.osc1Wave = presetParams.algMorph;
  }
  if (presetParams.waveFold !== undefined && presetParams.drive === undefined && presetParams.filterDrive === undefined) {
    base.drive = presetParams.waveFold;
    base.filterDrive = presetParams.waveFold;
  }
  if (presetParams.carAttack !== undefined && presetParams.ampAttack === undefined) {
    base.ampAttack = presetParams.carAttack;
  }
  if (presetParams.carDecay !== undefined && presetParams.ampDecay === undefined) {
    base.ampDecay = presetParams.carDecay;
  }
  if (presetParams.modAttack !== undefined && presetParams.fltAttack === undefined) {
    base.fltAttack = presetParams.modAttack;
  }
  if (presetParams.modDecay !== undefined && presetParams.fltDecay === undefined) {
    base.fltDecay = presetParams.modDecay;
  }
  if (presetParams.chorusDelayMix !== undefined && presetParams.delayMix === undefined) {
    base.delayMix = presetParams.chorusDelayMix;
    base.delayTimeMix = presetParams.chorusDelayMix;
  }
  if (presetParams.spaceReverbMix !== undefined && presetParams.reverbSpace === undefined) {
    base.reverbSpace = presetParams.spaceReverbMix;
  }
  if (presetParams.wowFlutter !== undefined && presetParams.detune === undefined) {
    base.detune = Math.min(0.4, presetParams.wowFlutter * 0.5);
    base.unisonDetune = base.detune;
  }

  // Ensure all alias groups match
  for (const group of PARAM_ALIAS_GROUPS) {
    const definedKey = group.find((k) => presetParams[k] !== undefined);
    if (definedKey && base[definedKey] !== undefined) {
      for (const alias of group) {
        if (presetParams[alias] === undefined) {
          base[alias] = base[definedKey];
        }
      }
    }
  }

  return base;
}
